/**
 * 九语页脚快照 — 由 scripts/sync-footer-from-gcsa.mjs 从主站 langs 生成，勿手改
 */
import type {
  GcsaFooterSnapshot,
  SiteFooterNavIcon,
  SiteFooterNavLink
} from './gcsaFooterContent';
import type { SiteFooterLocale } from './siteLocales';
import { GCSA_CONTACT_EMAIL, GCSA_TELEGRAM_URL } from './gcsaSiteOrigins';

const SYNCED_FOOTER_NAV_ORDER: SiteFooterNavIcon[] = [
  'home',
  'products',
  'aiResearchInstitute',
  'strategicPartners',
  'forums',
  'media',
  'about',
  'contact'
];

function buildSyncedNavLinks(
  labels: Record<SiteFooterNavIcon, string>,
  hrefs: Record<SiteFooterNavIcon, string>
): SiteFooterNavLink[] {
  return SYNCED_FOOTER_NAV_ORDER.map((key) => ({
    key,
    label: labels[key],
    href: hrefs[key],
    icon: key,
    external: true
  }));
}

const MAILTO = `mailto:${GCSA_CONTACT_EMAIL}`;

const GCSA_FOOTER_ZH_CN: GcsaFooterSnapshot = {
  title: "GCSA 联系方式",
  email: GCSA_CONTACT_EMAIL,
  emailLabel: "联系邮箱",
  counselNavAriaLabel: "法律顾问",
  copyright: "© Global Cybersecurity Alliance. 保留所有权利。",
  navAriaLabel: "页面导航",
  legalAriaLabel: "法律信息",
  socialAriaLabel: "社交媒体",
  socialPendingLabel: "即将开放",
  navLinks: buildSyncedNavLinks(
    {
      home: "首页",
      products: "生态产品矩阵",
      aiResearchInstitute: "AI与网络安全研究合作",
      strategicPartners: "战略合作伙伴",
      forums: "日常动态",
      media: "媒体与GCSA洞察",
      about: "关于联盟",
      contact: "加入联盟"
    },
    {
      home: "https://gcsa.org/",
      products: "https://gcsa.org/products",
      aiResearchInstitute: "https://gcsa.org/ai-research-institute",
      strategicPartners: "https://gcsa.org/strategic-partners",
      forums: "https://gcsa.org/forums",
      media: "https://media.gcsa.org",
      about: "https://gcsa.org/about",
      contact: "https://gcsa.org/contact"
    }
  ),
  socialLinks: [
    { id: 'telegram', label: "Telegram", href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: "隐私政策", href: "https://gcsa.org/privacy-policy", external: true },
    { label: "使用条款", href: "https://gcsa.org/terms-of-use", external: true },
    { label: "Cookie 设置", href: "https://gcsa.org/cookie-settings", external: true },
    { label: "contact@gcsa.com", href: MAILTO, external: true }
  ]
};

const GCSA_FOOTER_ZH_TW: GcsaFooterSnapshot = {
  title: "GCSA 聯繫方式",
  email: GCSA_CONTACT_EMAIL,
  emailLabel: "聯繫郵箱",
  counselNavAriaLabel: "法律顧問",
  copyright: "© Global Cybersecurity Alliance. 保留所有權利。",
  navAriaLabel: "頁面導航",
  legalAriaLabel: "法律信息",
  socialAriaLabel: "社交媒體",
  socialPendingLabel: "即將開放",
  navLinks: buildSyncedNavLinks(
    {
      home: "首頁",
      products: "生態產品矩陣",
      aiResearchInstitute: "AI與網絡安全研究合作",
      strategicPartners: "戰略合作夥伴",
      forums: "日常動態",
      media: "媒體與GCSA洞察",
      about: "關於聯盟",
      contact: "加入聯盟"
    },
    {
      home: "https://gcsa.org/zh-TW/",
      products: "https://gcsa.org/zh-TW/products",
      aiResearchInstitute: "https://gcsa.org/zh-TW/ai-research-institute",
      strategicPartners: "https://gcsa.org/zh-TW/strategic-partners",
      forums: "https://gcsa.org/zh-TW/forums",
      media: "https://media.gcsa.org",
      about: "https://gcsa.org/zh-TW/about",
      contact: "https://gcsa.org/zh-TW/contact"
    }
  ),
  socialLinks: [
    { id: 'telegram', label: "Telegram", href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: "隱私政策", href: "https://gcsa.org/zh-TW/privacy-policy", external: true },
    { label: "使用條款", href: "https://gcsa.org/zh-TW/terms-of-use", external: true },
    { label: "Cookie 設置", href: "https://gcsa.org/zh-TW/cookie-settings", external: true },
    { label: "contact@gcsa.com", href: MAILTO, external: true }
  ]
};

const GCSA_FOOTER_EN_US: GcsaFooterSnapshot = {
  title: "GCSA Contact",
  email: GCSA_CONTACT_EMAIL,
  emailLabel: "Email",
  counselNavAriaLabel: "Legal counsel",
  copyright: "© Global Cybersecurity Alliance. All rights reserved.",
  navAriaLabel: "Navigation",
  legalAriaLabel: "Legal",
  socialAriaLabel: "Social media",
  socialPendingLabel: "Coming soon",
  navLinks: buildSyncedNavLinks(
    {
      home: "Home",
      products: "Ecosystem Product Matrix",
      aiResearchInstitute: "AI & Cybersecurity Research Collaboration",
      strategicPartners: "Strategic Partners",
      forums: "Daily Updates",
      media: "Media & GCSA Insights",
      about: "About Alliance",
      contact: "Join the Alliance"
    },
    {
      home: "https://gcsa.org/en-US/",
      products: "https://gcsa.org/en-US/products",
      aiResearchInstitute: "https://gcsa.org/en-US/ai-research-institute",
      strategicPartners: "https://gcsa.org/en-US/strategic-partners",
      forums: "https://gcsa.org/en-US/forums",
      media: "https://media.gcsa.org",
      about: "https://gcsa.org/en-US/about",
      contact: "https://gcsa.org/en-US/contact"
    }
  ),
  socialLinks: [
    { id: 'telegram', label: "Telegram", href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: "Privacy Policy", href: "https://gcsa.org/en-US/privacy-policy", external: true },
    { label: "Terms of Use", href: "https://gcsa.org/en-US/terms-of-use", external: true },
    { label: "Cookie Settings", href: "https://gcsa.org/en-US/cookie-settings", external: true },
    { label: "contact@gcsa.com", href: MAILTO, external: true }
  ]
};

const GCSA_FOOTER_TH_TH: GcsaFooterSnapshot = {
  title: "ช่องทางติดต่อ GCSA",
  email: GCSA_CONTACT_EMAIL,
  emailLabel: "อีเมลติดต่อ",
  counselNavAriaLabel: "ที่ปรึกษากฎหมาย",
  copyright: "© Global Cybersecurity Alliance. สงวนลิขสิทธิ์",
  navAriaLabel: "การนำทาง",
  legalAriaLabel: "ข้อมูลทางกฎหมาย",
  socialAriaLabel: "โซเชียลมีเดีย",
  socialPendingLabel: "เร็วๆ นี้",
  navLinks: buildSyncedNavLinks(
    {
      home: "หน้าแรก",
      products: "เมทริกซ์ผลิตภัณฑ์ระบบนิเวศ",
      aiResearchInstitute: "ความร่วมมือวิจัย AI และความปลอดภัยไซเบอร์",
      strategicPartners: "พันธมิตรเชิงกลยุทธ์",
      forums: "อัปเดตประจำวัน",
      media: "สื่อและ GCSA Insights",
      about: "เกี่ยวกับพันธมิตร",
      contact: "เข้าร่วมพันธมิตร"
    },
    {
      home: "https://gcsa.org/th-TH/",
      products: "https://gcsa.org/th-TH/products",
      aiResearchInstitute: "https://gcsa.org/th-TH/ai-research-institute",
      strategicPartners: "https://gcsa.org/th-TH/strategic-partners",
      forums: "https://gcsa.org/th-TH/forums",
      media: "https://media.gcsa.org",
      about: "https://gcsa.org/th-TH/about",
      contact: "https://gcsa.org/th-TH/contact"
    }
  ),
  socialLinks: [
    { id: 'telegram', label: "Telegram", href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: "นโยบายความเป็นส่วนตัว", href: "https://gcsa.org/th-TH/privacy-policy", external: true },
    { label: "ข้อกำหนดการใช้งาน", href: "https://gcsa.org/th-TH/terms-of-use", external: true },
    { label: "การตั้งค่าคุกกี้", href: "https://gcsa.org/th-TH/cookie-settings", external: true },
    { label: "contact@gcsa.com", href: MAILTO, external: true }
  ]
};

const GCSA_FOOTER_ID_ID: GcsaFooterSnapshot = {
  title: "Kontak GCSA",
  email: GCSA_CONTACT_EMAIL,
  emailLabel: "Email kontak",
  counselNavAriaLabel: "Penasihat hukum",
  copyright: "© Global Cybersecurity Alliance. Hak cipta dilindungi.",
  navAriaLabel: "Navigasi",
  legalAriaLabel: "Informasi hukum",
  socialAriaLabel: "Media sosial",
  socialPendingLabel: "Segera hadir",
  navLinks: buildSyncedNavLinks(
    {
      home: "Beranda",
      products: "Matriks Produk Ekosistem",
      aiResearchInstitute: "Kolaborasi Riset AI & Keamanan Siber",
      strategicPartners: "Mitra Strategis",
      forums: "Pembaruan Harian",
      media: "Media & Wawasan GCSA",
      about: "Tentang Aliansi",
      contact: "Bergabung dengan Aliansi"
    },
    {
      home: "https://gcsa.org/id-ID/",
      products: "https://gcsa.org/id-ID/products",
      aiResearchInstitute: "https://gcsa.org/id-ID/ai-research-institute",
      strategicPartners: "https://gcsa.org/id-ID/strategic-partners",
      forums: "https://gcsa.org/id-ID/forums",
      media: "https://media.gcsa.org",
      about: "https://gcsa.org/id-ID/about",
      contact: "https://gcsa.org/id-ID/contact"
    }
  ),
  socialLinks: [
    { id: 'telegram', label: "Telegram", href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: "Kebijakan privasi", href: "https://gcsa.org/id-ID/privacy-policy", external: true },
    { label: "Ketentuan penggunaan", href: "https://gcsa.org/id-ID/terms-of-use", external: true },
    { label: "Pengaturan cookie", href: "https://gcsa.org/id-ID/cookie-settings", external: true },
    { label: "contact@gcsa.com", href: MAILTO, external: true }
  ]
};

const GCSA_FOOTER_KO_KR: GcsaFooterSnapshot = {
  title: "GCSA 연락처",
  email: GCSA_CONTACT_EMAIL,
  emailLabel: "비즈니스 이메일",
  counselNavAriaLabel: "법률 자문",
  copyright: "© Global Cybersecurity Alliance. 모든 권리 보유.",
  navAriaLabel: "페이지 내비게이션",
  legalAriaLabel: "법적 고지",
  socialAriaLabel: "소셜 미디어",
  socialPendingLabel: "곧 공개",
  navLinks: buildSyncedNavLinks(
    {
      home: "홈",
      products: "생태계 제품 매트릭스",
      aiResearchInstitute: "AI 및 사이버보안 연구 협력",
      strategicPartners: "전략적 파트너",
      forums: "일상 업데이트",
      media: "미디어 및 GCSA 인사이트",
      about: "얼라이언스 소개",
      contact: "얼라이언스 가입"
    },
    {
      home: "https://gcsa.org/ko-KR/",
      products: "https://gcsa.org/ko-KR/products",
      aiResearchInstitute: "https://gcsa.org/ko-KR/ai-research-institute",
      strategicPartners: "https://gcsa.org/ko-KR/strategic-partners",
      forums: "https://gcsa.org/ko-KR/forums",
      media: "https://media.gcsa.org",
      about: "https://gcsa.org/ko-KR/about",
      contact: "https://gcsa.org/ko-KR/contact"
    }
  ),
  socialLinks: [
    { id: 'telegram', label: "Telegram", href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: "개인정보 처리방침", href: "https://gcsa.org/ko-KR/privacy-policy", external: true },
    { label: "이용 약관", href: "https://gcsa.org/ko-KR/terms-of-use", external: true },
    { label: "쿠키 설정", href: "https://gcsa.org/ko-KR/cookie-settings", external: true },
    { label: "contact@gcsa.com", href: MAILTO, external: true }
  ]
};

const GCSA_FOOTER_JA_JP: GcsaFooterSnapshot = {
  title: "GCSA の連絡先",
  email: GCSA_CONTACT_EMAIL,
  emailLabel: "ビジネスメール",
  counselNavAriaLabel: "法律顧問",
  copyright: "© Global Cybersecurity Alliance. 無断転載を禁じます。",
  navAriaLabel: "ページナビゲーション",
  legalAriaLabel: "法的情報",
  socialAriaLabel: "ソーシャルメディア",
  socialPendingLabel: "近日公開",
  navLinks: buildSyncedNavLinks(
    {
      home: "ホーム",
      products: "エコシステム製品マトリクス",
      aiResearchInstitute: "AIとサイバーセキュリティ研究協力",
      strategicPartners: "戦略的パートナー",
      forums: "日常の更新",
      media: "メディアとGCSAインサイト",
      about: "アライアンスについて",
      contact: "アライアンスに参加"
    },
    {
      home: "https://gcsa.org/ja-JP/",
      products: "https://gcsa.org/ja-JP/products",
      aiResearchInstitute: "https://gcsa.org/ja-JP/ai-research-institute",
      strategicPartners: "https://gcsa.org/ja-JP/strategic-partners",
      forums: "https://gcsa.org/ja-JP/forums",
      media: "https://media.gcsa.org",
      about: "https://gcsa.org/ja-JP/about",
      contact: "https://gcsa.org/ja-JP/contact"
    }
  ),
  socialLinks: [
    { id: 'telegram', label: "Telegram", href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: "プライバシーポリシー", href: "https://gcsa.org/ja-JP/privacy-policy", external: true },
    { label: "利用規約", href: "https://gcsa.org/ja-JP/terms-of-use", external: true },
    { label: "Cookie 設定", href: "https://gcsa.org/ja-JP/cookie-settings", external: true },
    { label: "contact@gcsa.com", href: MAILTO, external: true }
  ]
};

const GCSA_FOOTER_VI_VN: GcsaFooterSnapshot = {
  title: "Cách liên hệ GCSA",
  email: GCSA_CONTACT_EMAIL,
  emailLabel: "Email liên hệ",
  counselNavAriaLabel: "Cố vấn pháp lý",
  copyright: "© Global Cybersecurity Alliance. Bảo lưu mọi quyền.",
  navAriaLabel: "Điều hướng",
  legalAriaLabel: "Thông tin pháp lý",
  socialAriaLabel: "Mạng xã hội",
  socialPendingLabel: "Sắp ra mắt",
  navLinks: buildSyncedNavLinks(
    {
      home: "Trang chủ",
      products: "Ma trận sản phẩm hệ sinh thái",
      aiResearchInstitute: "Hợp tác nghiên cứu AI & an ninh mạng",
      strategicPartners: "Đối tác chiến lược",
      forums: "Cập nhật hàng ngày",
      media: "Truyền thông & Góc nhìn GCSA",
      about: "Về liên minh",
      contact: "Tham gia liên minh"
    },
    {
      home: "https://gcsa.org/vi-VN/",
      products: "https://gcsa.org/vi-VN/products",
      aiResearchInstitute: "https://gcsa.org/vi-VN/ai-research-institute",
      strategicPartners: "https://gcsa.org/vi-VN/strategic-partners",
      forums: "https://gcsa.org/vi-VN/forums",
      media: "https://media.gcsa.org",
      about: "https://gcsa.org/vi-VN/about",
      contact: "https://gcsa.org/vi-VN/contact"
    }
  ),
  socialLinks: [
    { id: 'telegram', label: "Telegram", href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: "Chính sách quyền riêng tư", href: "https://gcsa.org/vi-VN/privacy-policy", external: true },
    { label: "Điều khoản sử dụng", href: "https://gcsa.org/vi-VN/terms-of-use", external: true },
    { label: "Cài đặt cookie", href: "https://gcsa.org/vi-VN/cookie-settings", external: true },
    { label: "contact@gcsa.com", href: MAILTO, external: true }
  ]
};

const GCSA_FOOTER_MS_MY: GcsaFooterSnapshot = {
  title: "Hubungan GCSA",
  email: GCSA_CONTACT_EMAIL,
  emailLabel: "E-mel hubungan",
  counselNavAriaLabel: "Penasihat undang-undang",
  copyright: "© Global Cybersecurity Alliance. Hak cipta terpelihara.",
  navAriaLabel: "Navigasi",
  legalAriaLabel: "Maklumat undang-undang",
  socialAriaLabel: "Media sosial",
  socialPendingLabel: "Akan datang",
  navLinks: buildSyncedNavLinks(
    {
      home: "Laman utama",
      products: "Matriks Produk Ekosistem",
      aiResearchInstitute: "Kerjasama Penyelidikan AI & Keselamatan Siber",
      strategicPartners: "Rakan Strategik",
      forums: "Kemas Kini Harian",
      media: "Media & Wawasan GCSA",
      about: "Perihal Perikatan",
      contact: "Sertai Perikatan"
    },
    {
      home: "https://gcsa.org/ms-MY/",
      products: "https://gcsa.org/ms-MY/products",
      aiResearchInstitute: "https://gcsa.org/ms-MY/ai-research-institute",
      strategicPartners: "https://gcsa.org/ms-MY/strategic-partners",
      forums: "https://gcsa.org/ms-MY/forums",
      media: "https://media.gcsa.org",
      about: "https://gcsa.org/ms-MY/about",
      contact: "https://gcsa.org/ms-MY/contact"
    }
  ),
  socialLinks: [
    { id: 'telegram', label: "Telegram", href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: "Dasar privasi", href: "https://gcsa.org/ms-MY/privacy-policy", external: true },
    { label: "Syarat penggunaan", href: "https://gcsa.org/ms-MY/terms-of-use", external: true },
    { label: "Tetapan kuki", href: "https://gcsa.org/ms-MY/cookie-settings", external: true },
    { label: "contact@gcsa.com", href: MAILTO, external: true }
  ]
};

export const GCSA_FOOTER_FROM_SITE_BY_LOCALE: Record<
  SiteFooterLocale,
  GcsaFooterSnapshot
> = {
  "zh-CN": GCSA_FOOTER_ZH_CN,
  "zh-TW": GCSA_FOOTER_ZH_TW,
  "en-US": GCSA_FOOTER_EN_US,
  "th-TH": GCSA_FOOTER_TH_TH,
  "id-ID": GCSA_FOOTER_ID_ID,
  "ko-KR": GCSA_FOOTER_KO_KR,
  "ja-JP": GCSA_FOOTER_JA_JP,
  "vi-VN": GCSA_FOOTER_VI_VN,
  "ms-MY": GCSA_FOOTER_MS_MY
};
